import MappingVisitor from "data/util/MappingVisitor";
import TableAccess from "data/util/TableAccess";
import TableStore from "data/TableStore";
import LoadObject from "data/LoadObject";
import invariant from "invariant";
import ClientId from "data/ClientId";
import { getCandidateFilter } from "data/selectors/entityFields";
import { infoTypeToFieldType } from "data/util/infoTypeToFieldType";
import SearchResultStore from "data/SearchResultStore";
import {
    clientFilteredList,
    clientFilteredSearch,
    ERR_NOT_YET_PROCESSED_BY_TABLESTORE,
} from "data/SuperRecordActions";
import {
    ERR_MULTIPLE_MATCHES_FOUND,
    ERR_NO_MATCH_FOUND,
    getPairedIdLO,
    getPairedIdLO_unqualified,
    getPairLO,
    setPairLO,
} from "data/MappingStore";
import Q from "lib/Q";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import entityMetaStore from "data/meta/entityMetaStore";
import SuperRecordListStore from "data/SuperRecordListStore";
import TableMappingVisitor from "data/TableMappingVisitor";

const buildQuery = (fieldKeys, instance) =>
    Q.conjunction(fieldKeys
        .map((fn) => entityFieldMetaStore.getInfo( // switch to fieldInfo
            instance.entityKey,
            fn,
        ))
        .filter((fi) => fi.type !== "FileField") // exclude file fields
        .map((fi) => Q.ref(fi.key).eq(
            Q.value(
                instance.get(fi.key),
                infoTypeToFieldType(fi.type),
            ),
        ))
    );

const loadGlobalMatch = (it, targetKey, candidateFilter) => {
    const targetInfo = entityMetaStore.getInfo(targetKey);
    const keyFields = targetInfo.keys[0].fieldKeys;
    const q = buildQuery(keyFields, it);
    let rawMatchFound = false;
    const lo = clientFilteredSearch(
        SearchResultStore,
        targetKey,
        q,
        null,
        2,
        r => {
            rawMatchFound = true;
            return r.isRecordEnabled() && candidateFilter(r);
        },
    );
    return {
        lo,
        rawMatchFound,
    };
};

class MappingWorkflowVisitor extends MappingVisitor {
    constructor(state) {
        super(state, new TableAccess(TableStore.getState()));
        this.__doItAgain = null;
    }

    getState() {
        return this.state;
    }

    _doItAgain(reason) {
        if (this.__doItAgain == null) {
            this.__doItAgain = new Set();
        }
        this.__doItAgain.add(reason);
    }

    isDoItAgain() {
        return this.__doItAgain != null;
    }

    getDoItAgainReason() {
        const reasons = [];
        this.__doItAgain.forEach(r => reasons.push(r));
        return reasons.join(", ");
    }

    loadIdPair(sourceId, recordKey, fieldKey, lo) {
        this.state = setPairLO(this.state, sourceId, recordKey, fieldKey, lo);
    }

    // Returns the ensured pair (NOT wrapped in an LO!)
    ensureIdPair(sourceId, recordKey, fieldKey, targetKey, global = false) {
        const curr = getPairLO(this.state, sourceId, recordKey, fieldKey);
        let v;
        if (curr.hasValue()) {
            v = curr.getValueEnforcing();
            invariant(
                v.key === targetKey,
                "Ensuring on a different target key?! '%s' vs '%s'",
                v.key,
                targetKey,
            );
        } else {
            v = {
                key: targetKey,
                id: ClientId.next(),
                global,
            };
        }
        this.loadIdPair(sourceId, recordKey, fieldKey, LoadObject.withValue(v));
        return v;
    }

    doRecord(ctx, target, excluded, source, type, edgeInfo) {
        if (excluded) return;
        super.doRecord(ctx, target, excluded, source, type, edgeInfo);
    }

    doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue) {
        this.loadIdPair(
            sourceValue.id,
            target.getKey(),
            fieldInfo.key,
            LoadObject.withValue({
                key: fieldInfo.otherEntityKey,
                id: target.get(fieldInfo).id,
            })
        );
        super.doMagicRoot(ctx, target, excluded, fieldInfo, sourceValue);
    }

    doGlobalAssoc(
        ctx,
        target,
        excluded,
        fieldInfo,
        sourceValue,
        createIfMissing,
    ) {
        const candidateFilter = getCandidateFilter(fieldInfo, target);
        let matchesPending = false;
        this.__forEach(sourceValue, it => {
            const {
                lo,
                rawMatchFound,
            } = loadGlobalMatch(
                this._getGarageGlobal(it, fieldInfo.otherEntityKey),
                fieldInfo.otherEntityKey,
                candidateFilter,
            );

            if (!lo.hasValue()) {
                if (lo.getError() === ERR_NOT_YET_PROCESSED_BY_TABLESTORE) {
                    /*
                     The issue is that clientFilteredSearch needs to get records
                     from TableStore, and TableStore needs to have the mapping
                     work done before it can execute mapping. That is, the fact
                     that this bit of code right here is running means we're
                     processing a LOADED action on some global being searched
                     for, but we have a cyclic dependency between MappingStore
                     and TableStore. Specifically, the mapping code needs to run
                     after TableStore has jammed the newly-LOADED record into
                     its state, but before TableStore reevaluates the mapping
                     operation currently in progress.
                     */
                    this._doItAgain("Load global into TableStore");
                }
                this.loadIdPair(
                    it.id,
                    target.getKey(),
                    fieldInfo.key,
                    LoadObject.loading("garbage"),
                );
                matchesPending = true;
                return;
            }
            const matches = lo.getRequiredValue();
            if (matches.length === 0) {
                if (createIfMissing && !rawMatchFound) {
                    this.ensureIdPair(
                        it.id,
                        target.getKey(),
                        fieldInfo.key,
                        fieldInfo.otherEntityKey,
                        true,
                    );
                } else {
                    this.loadIdPair(
                        it.id,
                        target.getKey(),
                        fieldInfo.key,
                        LoadObject.withError(ERR_NO_MATCH_FOUND),
                    );
                }
            } else if (matches.length === 1) {
                // do we need to process a previous match?
                // do we need to clean up a previously created value?
                this.loadIdPair(
                    it.id,
                    target.getKey(),
                    fieldInfo.key,
                    LoadObject.withValue({
                        key: fieldInfo.otherEntityKey,
                        id: matches[0].id,
                        global: true,
                    }),
                );
            } else {
                this.loadIdPair(
                    it.id,
                    target.getKey(),
                    fieldInfo.key,
                    LoadObject.withError(ERR_MULTIPLE_MATCHES_FOUND),
                );
            }
        });
        if (matchesPending) return;
        super.doGlobalAssoc(
            ctx,
            target,
            excluded,
            fieldInfo,
            sourceValue,
            createIfMissing
        );
    }

    doComposition(ctx, target, excluded, fieldInfo, sourceValue) {
        let hasNew = false;
        this.__forEach(sourceValue, it => {
            const p = this.ensureIdPair(
                it.id,
                target.getKey(),
                fieldInfo.key,
                fieldInfo.otherEntityKey,
            );
            hasNew = hasNew || !this.access.hasEntityRecord(p.key, p.id);
        });
        // Do we need to wait for TableStore to do it's thing?
        if (!hasNew) {
            super.doComposition(ctx, target, excluded, fieldInfo, sourceValue);
        }
    }

    _getGarageGlobal(sourceGlobal, targetEK) {
        const garageAccess = new TableAccess(TableStore.getInitialState());
        const garageId = ClientId.next();
        garageAccess.createEntityRecord(targetEK, garageId);
        const garageGlobal = garageAccess.getEntityProxy(targetEK, garageId);
        const tmv = new TableMappingVisitor({
            target: garageGlobal,
            source: sourceGlobal,
            modelLO: LoadObject.withValue(null),
        }, garageAccess);
        tmv.execute();
        return garageGlobal;
    }

    doAutomanaged(
        ctx,
        target,
        excluded,
        fieldInfo,
        autoManagedByFieldInfo,
        sourceValue,
        sourceManagedByGetter,
    ) {
        // Get the  list of target global records
        const gEK = autoManagedByFieldInfo.otherEntityKey;
        const targetGlobalsLO = clientFilteredList(
            SuperRecordListStore,
            this.access,
            gEK,
            // don't test here, so we only have to assemble the list once...
        );
        if (!targetGlobalsLO.hasValue()) return;
        const targetGlobals = targetGlobalsLO.getValueEnforcing();

        // For each source, see if there's a target, and record it if there is
        const globalInfo = entityMetaStore.getInfo(gEK);
        const keyFields = globalInfo.keys ? globalInfo.keys[0].fieldKeys : [];
        const amtl = this.__getAutomanagedTargetLookup(target, fieldInfo);
        if (amtl == null) return;
        this.__forEach(sourceValue, s => {
            const sourceGlobal = sourceManagedByGetter(s);
            const garageGlobal = this._getGarageGlobal(sourceGlobal, gEK);
            const q = buildQuery(keyFields, garageGlobal);
            const targetGlobal = targetGlobals.find(q.test);
            if (targetGlobal == null) return;
            if (!amtl.hasOwnProperty(targetGlobal.id)) return;
            // child -> child
            this.loadIdPair(
                s.id,
                target.entityKey,
                fieldInfo.key,
                LoadObject.withValue({
                    key: fieldInfo.otherEntityKey,
                    id: amtl[targetGlobal.id].id,
                })
            );
            // global -> global
            this.loadIdPair(
                sourceGlobal.id,
                amtl[targetGlobal.id].getKey(),
                fieldInfo.autoManageKey,
                LoadObject.withValue({
                    key: gEK,
                    id: targetGlobal.id,
                    global: true,
                }),
            );
        });

        // Recurse
        super.doAutomanaged(
            ctx,
            target,
            excluded,
            fieldInfo,
            autoManagedByFieldInfo,
            sourceValue,
            sourceManagedByGetter
        );
    }

    doCrossTree(ctx, target, excluded, fieldInfo, sourceValue) {
        this.__forEach(sourceValue, it => {
            // These _deliberately_ don't use the helpers. Composed children are
            // managed synchronously throughout all the mapping machinery, and
            // CTAs are always between newly-created composed children, so the
            // data is guaranteed available at this point. Since TableStore will
            // end up updating (to create the children), the workflow will end
            // up running again, but there's no point in waiting, since we can
            // make the above guarantee.
            let lo = getPairedIdLO(
                this.state,
                it.id,
                fieldInfo.entityKey, // compositions are only entity-scoped, not record-scoped
                fieldInfo.key,
            );
            if (lo.hasValue()) return; // gravy!
            lo = getPairedIdLO_unqualified(
                this.state,
                it.id,
            );
            this.loadIdPair(
                it.id,
                target.getKey(),
                fieldInfo.key,
                LoadObject.withValue({
                    key: fieldInfo.otherEntityKey,
                    id: lo.getValueEnforcing(),
                })
            );
        });
    }

    shouldDoField(ctx, target, excluded, fieldInfo) {
        // we don't care about fields at all
        return false;
    }

}

export default MappingWorkflowVisitor;



// WEBPACK FOOTER //
// ./app/data/MappingWorkflowVisitor.js